import { useMemo, useState } from "react";
import { Card } from "antd";
import { toast } from "react-hot-toast";
import Footer from "../components/Footer";
import Header from "../components/Header";
import { useTranslation } from "react-i18next";
import StandardButton from "../components/StandardButton";

const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));

const hexToRgb = (hex) => {
  const m = hex.trim().replace(/^#/, "");
  let r;
  let g;
  let b;
  let a = 1;
  if (/^[0-9a-f]{3}$/i.test(m)) {
    r = parseInt(m[0] + m[0], 16);
    g = parseInt(m[1] + m[1], 16);
    b = parseInt(m[2] + m[2], 16);
  } else if (/^[0-9a-f]{4}$/i.test(m)) {
    r = parseInt(m[0] + m[0], 16);
    g = parseInt(m[1] + m[1], 16);
    b = parseInt(m[2] + m[2], 16);
    a = parseInt(m[3] + m[3], 16) / 255;
  } else if (/^[0-9a-f]{6}$/i.test(m)) {
    r = parseInt(m.slice(0, 2), 16);
    g = parseInt(m.slice(2, 4), 16);
    b = parseInt(m.slice(4, 6), 16);
  } else if (/^[0-9a-f]{8}$/i.test(m)) {
    r = parseInt(m.slice(0, 2), 16);
    g = parseInt(m.slice(2, 4), 16);
    b = parseInt(m.slice(4, 6), 16);
    a = parseInt(m.slice(6, 8), 16) / 255;
  } else {
    return null;
  }
  return { r, g, b, a };
};

const parseRgbString = (input) => {
  const m = input.trim().match(
    /^rgba?\(\s*([\d.]+)\s*[, ]\s*([\d.]+)\s*[, ]\s*([\d.]+)\s*(?:[,/]\s*([\d.]+%?))?\s*\)$/i
  );
  if (!m) return null;
  const r = Math.round(clamp(parseFloat(m[1]), 0, 255));
  const g = Math.round(clamp(parseFloat(m[2]), 0, 255));
  const b = Math.round(clamp(parseFloat(m[3]), 0, 255));
  let a = 1;
  if (m[4] !== undefined) {
    a = m[4].endsWith("%") ? parseFloat(m[4]) / 100 : parseFloat(m[4]);
  }
  return { r, g, b, a: clamp(a, 0, 1) };
};

const hslToRgb = (h, s, l, a = 1) => {
  const S = s / 100;
  const L = l / 100;
  const k = (n) => (n + h / 30) % 12;
  const f = (n) => {
    const c = Math.min(L, 1 - L) * S;
    return L - c * Math.max(-1, Math.min(k(n) - 3, 9 - k(n), 1));
  };
  return {
    r: Math.round(f(0) * 255),
    g: Math.round(f(8) * 255),
    b: Math.round(f(4) * 255),
    a,
  };
};

const parseHslString = (input) => {
  const m = input.trim().match(
    /^hsla?\(\s*([\d.]+)(deg|rad|turn)?\s*[, ]\s*([\d.]+)%\s*[, ]\s*([\d.]+)%\s*(?:[,/]\s*([\d.]+%?))?\s*\)$/i
  );
  if (!m) return null;
  let h = parseFloat(m[1]);
  const unit = (m[2] || "deg").toLowerCase();
  if (unit === "rad") h = (h * 180) / Math.PI;
  else if (unit === "turn") h *= 360;
  h = ((h % 360) + 360) % 360;
  const s = clamp(parseFloat(m[3]), 0, 100);
  const l = clamp(parseFloat(m[4]), 0, 100);
  let a = 1;
  if (m[5] !== undefined) {
    a = m[5].endsWith("%") ? parseFloat(m[5]) / 100 : parseFloat(m[5]);
  }
  return hslToRgb(h, s, l, clamp(a, 0, 1));
};

const parseAny = (raw) => {
  const input = raw.trim();
  if (!input) return null;
  if (input.startsWith("#") || /^[0-9a-f]{3,8}$/i.test(input)) {
    return hexToRgb(input);
  }
  if (/^rgba?\(/i.test(input)) return parseRgbString(input);
  if (/^hsla?\(/i.test(input)) return parseHslString(input);
  return null;
};

const toHex = (n) => clamp(Math.round(n), 0, 255).toString(16).padStart(2, "0");
const formatHex = ({ r, g, b }) => `#${toHex(r)}${toHex(g)}${toHex(b)}`;

// WCAG 2.x relative luminance — sRGB → linear, then ITU-R BT.709 weights.
// https://www.w3.org/TR/WCAG21/#dfn-relative-luminance
const channelToLinear = (c) => {
  const s = c / 255;
  return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
};

const relativeLuminance = ({ r, g, b }) =>
  0.2126 * channelToLinear(r) +
  0.7152 * channelToLinear(g) +
  0.0722 * channelToLinear(b);

// Flatten a (possibly translucent) foreground over a fully opaque background
// so the ratio reflects what users actually see — WCAG ratios are defined
// against the rendered pixel, not the raw color value.
const flatten = (fg, bg) => {
  const a = fg.a;
  if (a >= 1) return { r: fg.r, g: fg.g, b: fg.b };
  return {
    r: Math.round(fg.r * a + bg.r * (1 - a)),
    g: Math.round(fg.g * a + bg.g * (1 - a)),
    b: Math.round(fg.b * a + bg.b * (1 - a)),
  };
};

const contrastRatio = (fg, bg) => {
  const flatBg = { r: bg.r, g: bg.g, b: bg.b };
  const flatFg = flatten(fg, flatBg);
  const L1 = relativeLuminance(flatFg);
  const L2 = relativeLuminance(flatBg);
  const lighter = Math.max(L1, L2);
  const darker = Math.min(L1, L2);
  return (lighter + 0.05) / (darker + 0.05);
};

const swap = (a, b) => [b, a];

const ColorContrastChecker = () => {
  const { t } = useTranslation();
  const [fgInput, setFgInput] = useState("#1f2937");
  const [bgInput, setBgInput] = useState("#ffffff");

  const fg = useMemo(() => parseAny(fgInput), [fgInput]);
  const bg = useMemo(() => parseAny(bgInput), [bgInput]);

  const ratio = useMemo(() => {
    if (!fg || !bg) return null;
    return contrastRatio(fg, { ...bg, a: 1 });
  }, [fg, bg]);

  const ratioText = ratio ? `${ratio.toFixed(2)} : 1` : "—";

  // WCAG 2.x success criteria 1.4.3 (AA) / 1.4.6 (AAA).
  const grades = ratio
    ? {
        aaNormal: ratio >= 4.5,
        aaLarge: ratio >= 3,
        aaaNormal: ratio >= 7,
        aaaLarge: ratio >= 4.5,
        uiComponent: ratio >= 3, // 1.4.11 non-text contrast
      }
    : null;

  const fgPreview = fg
    ? `rgba(${fg.r}, ${fg.g}, ${fg.b}, ${Number(fg.a.toFixed(3))})`
    : "transparent";
  const bgPreview = bg ? formatHex(bg) : "#ffffff";

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} ${t("copy_success")}`);
    } catch {
      toast.error(t("failed_to_copy"));
    }
  };

  const onSwap = () => {
    const [nextFg, nextBg] = swap(fgInput, bgInput);
    setFgInput(nextFg);
    setBgInput(nextBg);
  };

  const Badge = ({ pass, label, sub }) => (
    <div
      className={`flex flex-col items-center justify-center rounded-md border p-3 text-center ${
        pass
          ? "bg-emerald-50 border-emerald-200 text-emerald-800"
          : "bg-rose-50 border-rose-200 text-rose-800"
      }`}
    >
      <span className="text-xs font-semibold uppercase tracking-wide">
        {label}
      </span>
      <span className="mt-1 text-sm font-bold">
        {pass ? t("color_contrast_pass") : t("color_contrast_fail")}
      </span>
      {sub && <span className="mt-0.5 text-[11px] opacity-80">{sub}</span>}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-indigo-50 to-white p-0 text-gray-800">
      <Header />
      <div className="max-w-5xl mx-auto p-4">
        <Card className="shadow-lg rounded-xl">
          <div className="p-4 flex flex-col gap-4">
            <h1 className="text-2xl font-bold text-indigo-700">
              {t("color_contrast_title")}
            </h1>
            <p className="text-gray-600">{t("color_contrast_intro")}</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-gray-700">
                  {t("color_contrast_fg_label")}
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={fgInput}
                    onChange={(e) => setFgInput(e.target.value)}
                    placeholder="#1f2937, rgb(31,41,55), hsl(220, 26%, 17%)"
                    className="flex-1 p-2 font-mono border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                  />
                  <input
                    type="color"
                    value={fg ? formatHex(fg) : "#000000"}
                    onChange={(e) => setFgInput(e.target.value)}
                    className="w-12 h-10 p-0 border border-gray-300 rounded-md cursor-pointer bg-white"
                  />
                </div>
                {!fg && (
                  <span className="text-xs text-rose-600">
                    {t("color_contrast_invalid")}
                  </span>
                )}
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-gray-700">
                  {t("color_contrast_bg_label")}
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={bgInput}
                    onChange={(e) => setBgInput(e.target.value)}
                    placeholder="#ffffff, rgb(255,255,255), hsl(0, 0%, 100%)"
                    className="flex-1 p-2 font-mono border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                  />
                  <input
                    type="color"
                    value={bg ? formatHex(bg) : "#ffffff"}
                    onChange={(e) => setBgInput(e.target.value)}
                    className="w-12 h-10 p-0 border border-gray-300 rounded-md cursor-pointer bg-white"
                  />
                </div>
                {!bg && (
                  <span className="text-xs text-rose-600">
                    {t("color_contrast_invalid")}
                  </span>
                )}
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <StandardButton
                onClick={onSwap}
                type="secondary"
                variant="outlined"
                size="middle"
              >
                {t("color_contrast_swap")}
              </StandardButton>
              <StandardButton
                onClick={() => copy(ratioText, t("color_contrast_ratio_label"))}
                type="secondary"
                variant="outlined"
                size="middle"
                disabled={!ratio}
              >
                {t("color_contrast_copy_ratio")}
              </StandardButton>
            </div>

            <div
              className="rounded-md border border-indigo-200 shadow-inner p-6"
              style={{ backgroundColor: bgPreview, color: fgPreview }}
            >
              <p className="text-base leading-relaxed">
                {t("color_contrast_sample_normal")}
              </p>
              <p className="mt-2 text-2xl font-semibold leading-snug">
                {t("color_contrast_sample_large")}
              </p>
              <p className="mt-2 text-lg font-bold leading-snug">
                {t("color_contrast_sample_bold")}
              </p>
            </div>

            <div className="bg-indigo-50 p-4 rounded-md border border-indigo-200 flex flex-col gap-3">
              <div className="flex items-baseline justify-between flex-wrap gap-2">
                <span className="text-sm font-semibold text-indigo-700 uppercase tracking-wide">
                  {t("color_contrast_ratio_label")}
                </span>
                <span className="font-mono text-3xl font-bold text-indigo-900">
                  {ratioText}
                </span>
              </div>
              {grades && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                  <Badge
                    pass={grades.aaNormal}
                    label="AA"
                    sub={t("color_contrast_normal_text")}
                  />
                  <Badge
                    pass={grades.aaLarge}
                    label="AA"
                    sub={t("color_contrast_large_text")}
                  />
                  <Badge
                    pass={grades.aaaNormal}
                    label="AAA"
                    sub={t("color_contrast_normal_text")}
                  />
                  <Badge
                    pass={grades.aaaLarge}
                    label="AAA"
                    sub={t("color_contrast_large_text")}
                  />
                  <Badge
                    pass={grades.uiComponent}
                    label={t("color_contrast_ui_label")}
                    sub={t("color_contrast_ui_sub")}
                  />
                </div>
              )}
            </div>

            <div className="text-xs text-gray-500 leading-relaxed">
              <p>{t("color_contrast_note_large")}</p>
              <p>{t("color_contrast_note_alpha")}</p>
              <p>{t("color_contrast_note_privacy")}</p>
            </div>
          </div>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default ColorContrastChecker;
